import request from './request';
import requestWithoutTimeout from './requestWithoutTimeout';

/** Let axios set multipart boundary (see blog/gallery services). */
const formDataConfig = { headers: { 'Content-Type': undefined } };

const orderService = {
  /** Non-paginated index (same data as paginate without paging; see nextjs-admin-order-management.md). */
  getList: (params) => request.get('dashboard/admin/orders', { params }),
  getAll: (params) =>
    request.get('dashboard/admin/orders/paginate', { params }),
  getTrackingPaginate: (params) =>
    request.get('dashboard/admin/orders/tracking/paginate', { params }),
  getById: (id, params) =>
    request.get(`dashboard/admin/orders/${id}`, { params }),
  deleteById: (id) =>
    request.delete(`dashboard/admin/orders/${id}`),
  export: (params) =>
    requestWithoutTimeout.get(`dashboard/admin/order/export`, { params }),
  create: (data) => request.post('dashboard/admin/orders', data, {}),
  update: (id, data) => request.put(`dashboard/admin/orders/${id}`, data),
  calculate: (params) =>
    request.get(`dashboard/admin/order/products/calculate${params}`),
  /**
   * Body: `{ order_status }` or `{ status }` (docs/order-management-admin-guide.md).
   * Sends both keys when only `order_status` is provided for backend compatibility.
   */
  patchOrderStatus: (id, data = {}) => {
    const body = { ...data };
    if (body.order_status != null && body.status == null) {
      body.status = body.order_status;
    }
    if (body.status != null && body.order_status == null) {
      body.order_status = body.status;
    }
    return request.patch(`dashboard/admin/orders/${id}/status`, body, {});
  },
  /** `payment_status`: pending | paid | failed */
  patchPaymentStatus: (id, data) =>
    request.patch(`dashboard/admin/orders/${id}/payment-status`, data, {}),
  /**
   * What the address the order was placed from resolves to — city, network
   * operator, reverse DNS. Looked up on demand and cached on the order, so the
   * second call for one order costs nothing. `refresh` takes it again.
   */
  getIpIntel: (id, { refresh = false } = {}) =>
    request.get(`dashboard/admin/orders/${id}/ip-intel`, {
      params: refresh ? { refresh: 1 } : {},
    }),
  /**
   * Security evidence for one order: risk assessment, what the customer typed
   * at checkout, device and network, payment evidence, customer history,
   * orders sharing a phone / address / IP, and the audit trail.
   */
  getSecurity: (id) => request.get(`dashboard/admin/orders/${id}/security`),
  /** Body `{ method: 'phone_call'|'whatsapp'|'email'|'in_person', note? }`. */
  verifyAddress: (id, data) =>
    request.post(`dashboard/admin/orders/${id}/verify-address`, data, {}),
  /**
   * Send one of the shop's own order emails to this order's customer.
   *
   * `key` is a catalogue key the backend allowlists; the recipient is read from
   * the order server-side and cannot be set from here.
   */
  sendFollowUpEmail: (id, data) =>
    request.post(`dashboard/admin/orders/${id}/send-email`, data, {}),
  /**
   * What has already been emailed about this order, counted per template.
   *
   * Automatic and manual sends land in the same log and are not separated: the
   * customer received the same email either way, and the count is about how
   * many times they have been contacted.
   */
  getOrderEmails: (id) => request.get(`dashboard/admin/orders/${id}/emails`),
  /** `tracking_id`, `tracking_url`, `estimated_delivery` (ISO date). */
  patchTracking: (id, data) =>
    request.patch(`dashboard/admin/orders/${id}/tracking`, data, {}),
  /** Legacy: pause/canceled notes and older backends. */
  updateStatus: (id, data) =>
    request.patch(`dashboard/admin/orders/${id}/update-status`, data, {}),
  createStatus: (id, data) =>
    request.post(`dashboard/admin/orders/${id}/create-status`, data),
  updateDelivery: (id, params) =>
    request.post(`dashboard/admin/order/${id}/deliveryman`, {}, { params }),
  delete: (data) =>
    request.delete(`dashboard/admin/orders/delete`, { data }),
  /** Bulk delete; body `{ ids: string[] }` (admin). */
  deleteBulk: (ids) =>
    request.delete(`dashboard/admin/orders/delete`, { data: { ids } }),
  dropAll: () => request.get(`dashboard/admin/orders/drop/all`),
  restoreAll: () => request.get(`dashboard/admin/orders/restore/all`),
  getAllUserOrder: (id, params) =>
    request.get(`dashboard/admin/user-orders/${id}/paginate`, { params }),
  getUserTopProducts: (id, params) =>
    request.get(`dashboard/admin/user-orders/${id}`, { params }),
  replaceProduct: (id, data) =>
    request.put(`dashboard/admin/orders/${id}`, data, {}),
  /** Canonical replace line (see docs/FRONTEND_ORDER_GUIDE.md §9). */
  replaceOrderLine: (orderId, lineStockId, data) =>
    request.put(
      `dashboard/admin/orders/${orderId}/replace-line/${lineStockId}`,
      data,
    ),
  getTracking: (orderId) =>
    request.get(`dashboard/admin/orders/${orderId}/tracking`),
  createTracking: (orderId, data) =>
    request.post(`dashboard/admin/orders/${orderId}/tracking`, data),
  updateTracking: (orderId, data) =>
    request.put(`dashboard/admin/orders/${orderId}/tracking`, data),
  createRefund: (id, data) =>
    request.post(`dashboard/admin/orders/${id}/create-refund`, data),
  /** Multipart field name: `receipt` (optional gallery upload per ORDERS_REFUNDS doc §3.2b). */
  uploadTrackingReceipt: (orderId, file) => {
    const formData = new FormData();
    formData.append('receipt', file);
    return request.post(
      `dashboard/admin/tracking/${orderId}/receipt`,
      formData,
      formDataConfig,
    );
  },
  getByParentId: (id, params) =>
    request.get(`dashboard/admin/orders/${id}/get-all`),

  getKlarnaStatus: (id) =>
    request.get(`dashboard/admin/orders/${id}/klarna/status`),
  captureKlarna: (id, data = {}) =>
    request.post(`dashboard/admin/orders/${id}/klarna/capture`, data),
  getKlarnaPayout: (id) =>
    request.get(`dashboard/admin/orders/${id}/klarna/payout`),
};

export default orderService;
